/**
 * Shared geometry for fixed-position dropdown menus anchored to a trigger
 * (TEND button PRUNE menu, video Clips/Posts dropdown, list-view quick-fill).
 *
 * The contract that keeps menus on screen regardless of their real height:
 * the caller passes an *estimated* natural height, used only to decide which
 * side of the anchor to open on. The returned maxHeight is the hard cap for
 * the chosen side — the menu must apply it (flex column, scrollable middle)
 * so a taller-than-estimated menu clamps and scrolls instead of running off
 * the viewport. Above-the-anchor placement is bottom-anchored so the menu
 * grows upward from the anchor no matter how tall it really is.
 */
import type { CSSProperties } from 'react';

export interface DropdownPosition {
  left: number;
  /** Set when opening below the anchor (px from viewport top). */
  top?: number;
  /** Set when opening above the anchor (px from viewport bottom). */
  bottom?: number;
  /** Hard height cap for the chosen side — apply to the menu wrapper. */
  maxHeight: number;
}

/** Minimum gap between the menu and every viewport edge. */
export const DROPDOWN_VIEWPORT_MARGIN = 20;
/** Gap between the anchor and the menu. */
export const DROPDOWN_ANCHOR_GAP = 8;
/** Floor for maxHeight so degenerate (very short) windows still get a usable menu. */
const MIN_MENU_HEIGHT = 200;

export function positionDropdown(
  anchor: DOMRect,
  menuWidth: number,
  naturalHeight: number,
  options: { align?: 'right' | 'center' } = {},
): DropdownPosition {
  const vw = window.innerWidth;
  const vh = window.innerHeight;

  let left =
    options.align === 'center'
      ? (anchor.left + anchor.right) / 2 - menuWidth / 2
      : anchor.right - menuWidth;
  left = Math.min(left, vw - menuWidth - DROPDOWN_VIEWPORT_MARGIN);
  left = Math.max(left, DROPDOWN_VIEWPORT_MARGIN);

  const spaceBelow = vh - anchor.bottom - DROPDOWN_ANCHOR_GAP - DROPDOWN_VIEWPORT_MARGIN;
  const spaceAbove = anchor.top - DROPDOWN_ANCHOR_GAP - DROPDOWN_VIEWPORT_MARGIN;
  const openBelow = spaceBelow >= naturalHeight || spaceBelow >= spaceAbove;

  return openBelow
    ? {
        left,
        top: anchor.bottom + DROPDOWN_ANCHOR_GAP,
        maxHeight: Math.max(MIN_MENU_HEIGHT, spaceBelow),
      }
    : {
        left,
        bottom: vh - anchor.top + DROPDOWN_ANCHOR_GAP,
        maxHeight: Math.max(MIN_MENU_HEIGHT, spaceAbove),
      };
}

/**
 * Inline style for the portal wrapper of a positioned dropdown. The flex
 * column is load-bearing: it lets the menu (min-h-0 flex child) shrink to
 * maxHeight and scroll internally instead of overflowing the viewport.
 */
export function dropdownWrapperStyle(
  position: DropdownPosition,
  zIndex: number,
): CSSProperties {
  return {
    position: 'fixed',
    left: position.left,
    top: position.top,
    bottom: position.bottom,
    maxHeight: position.maxHeight,
    display: 'flex',
    flexDirection: 'column',
    zIndex,
  };
}
